// src/components/tuition/SuperAdminTuitionManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaPlus,
  FaSlidersH,
  FaEnvelope,
  FaCheckCircle,
  FaClock,
  FaSyncAlt,
  FaTrash,
  FaUserTie,
  FaSearch,
  FaGraduationCap,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaVideo,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../../context/AppThemeContext";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { SUBSCRIPTION_PLANS, formatInr } from "../../data/subscriptionPlans";
import {
  TUITION_MODULE_DEFINITIONS,
  TUITION_MODULE_KEYS,
} from "./tuitionMockData";

export default function SuperAdminTuitionManagement() {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Onboard Modal State
  const [onboardModal, setOnboardModal] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    name: "",
    ownerName: "",
    ownerEmail: "",
    ownerPhone: "",
    subdomain: "",
    city: "New Delhi",
    plan: "FREE_TRIAL",
    enabledModules: {
      [TUITION_MODULE_KEYS.STUDENT_MANAGEMENT]: true,
      [TUITION_MODULE_KEYS.TEACHER_MANAGEMENT]: true,
      [TUITION_MODULE_KEYS.ATTENDANCE]: true,
      [TUITION_MODULE_KEYS.ASSESSMENTS]: true,
      [TUITION_MODULE_KEYS.FEES_FINANCIALS]: true,
      [TUITION_MODULE_KEYS.ONLINE_CLASSES]: true,
      [TUITION_MODULE_KEYS.PARENT_REMINDERS]: true,
    },
  });


  const unwrapList = (response) => {
    const payload = response?.data?.data ?? response?.data;
    if (Array.isArray(payload)) return payload;
    return payload?.tuitions || payload?.institutes || payload?.items || [];
  };

  const normalizeInstitute = (institute = {}) => {
    const activationStatus = String(institute.activationStatus || institute.status || "").toUpperCase();
    return {
      ...institute,
      name: institute.name || institute.instituteName || "",
      ownerName: institute.ownerName || institute.founderName || "",
      tenantType: institute.tenantType || institute.tenant_type || "TUITION",
      activationStatus: activationStatus === "ACTIVE" ? "ACTIVATED" : activationStatus,
      trialStartDate: institute.trialStartDate || institute.trialStartedAt || institute.trial_started_at || "",
      trialExpiryDate: institute.trialExpiryDate || institute.trialExpiresAt || institute.trial_expires_at || "",
      extensionPending: institute.extensionPending === true || institute.extension_pending === true,
    };
  };

  const formatDate = (value) => {
    if (!value) return "Not provided";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-IN");
  };

  const loadInstitutes = async () => {
    setLoading(true);
    try {
      const response = await api.get(API_PATHS.tuition.institutes());
      setInstitutes(unwrapList(response).map(normalizeInstitute));
    } catch (error) {
      setInstitutes([]);
      toast.error(error?.message || "Unable to load tuition institutes from the database.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInstitutes(); }, []);

  const filteredInstitutes = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return (institutes || []).filter((inst) => {
      const matchSearch =
        (inst.name || "").toLowerCase().includes(query) ||
        (inst.ownerName || "").toLowerCase().includes(query) ||
        (inst.subdomain || "").toLowerCase().includes(query);
      const matchStatus = statusFilter === "ALL" || inst.activationStatus === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [institutes, searchTerm, statusFilter]);

  // -------------------------------------------------------------
  // ONBOARDING HANDLER
  // -------------------------------------------------------------
  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    if (!onboardForm.name.trim() || !onboardForm.ownerName.trim() || !onboardForm.ownerEmail.trim()) {
      toast.error("Institute name, owner name, and owner email are required.");
      return;
    }

    const request = {
      instituteName: onboardForm.name.trim(),
      ownerName: onboardForm.ownerName.trim(),
      ownerEmail: onboardForm.ownerEmail.trim(),
      ownerPhone: onboardForm.ownerPhone.trim(),
      subdomain: (onboardForm.subdomain || onboardForm.name.toLowerCase().replace(/[^a-z0-9]/g, "")).trim(),
      city: onboardForm.city.trim(),
      plan: onboardForm.plan,
      tenantType: "TUITION",
      trialDays: 7,
      enabledModules: { ...onboardForm.enabledModules },
    };
    try {
      const response = await api.post(API_PATHS.tuition.institutes(), request);
      const payload = response?.data?.data ?? response?.data ?? {};
      const newTuition = normalizeInstitute(payload.institute || payload.tuition || payload);
      setOnboardModal(false);
      await loadInstitutes();
      toast.success(`Tuition '${newTuition.name || request.instituteName}' onboarded and the activation email was queued.`);
    } catch (error) {
      toast.error(error?.message || "Unable to onboard this tuition institute.");
    }
  };


  // -------------------------------------------------------------
  // ACTIVATION ACTIONS
  // -------------------------------------------------------------
  const handleResendActivation = async (institute) => {
    try {
      await api.post(API_PATHS.tuition.resendActivation(institute.id));
      toast.success(`Activation email resent to ${institute.ownerEmail}.`);
    } catch (error) { toast.error(error?.message || "Unable to resend the activation email."); }
  };

  const handleDeleteInstitute = async (instituteId) => {
    const confirm = window.confirm("Are you sure you want to remove this tuition institute from ClassNX?");
    if (!confirm) return;
    try {
      await api.delete(API_PATHS.tuition.instituteById(instituteId));
      setInstitutes((current) => current.filter((i) => i.id !== instituteId));
      toast.info("Tuition institute removed.");
    } catch (error) { toast.error(error?.message || "Unable to remove the institute."); }
  };

  return (
    <div className={`min-h-screen pb-16 pt-4 px-3 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Top Header */}
        <div className={`rounded-3xl border p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-sky-600 to-indigo-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-sky-600/20">
              <FaBuilding />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-sky-100 dark:bg-sky-950/60 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-sky-700 dark:text-sky-300">
                  Super Admin Management
                </span>
                <span className="text-xs text-slate-400 font-semibold">
                  Multi-Tenant Platform
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 mt-1">
                Tuition Classes & Coaching Institutes
              </h1>
              <p className="text-xs text-slate-500">
                Onboard new tuition classes, send activation emails, and customize feature modules per institute.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadInstitutes}
              disabled={loading}
              className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border transition disabled:cursor-wait disabled:opacity-60 ${isDarkMode ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}
              title="Refresh tuition institutes"
              aria-label="Refresh tuition institutes"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              onClick={() => setOnboardModal(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-xs font-bold text-white shadow-md shadow-sky-600/25 hover:bg-sky-700 transition"
            >
              <FaPlus /> Onboard New Tuition
            </button>
          </div>
        </div>

        {/* KPI Counter Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Tuitions</span>
            <div className="mt-2 text-2xl font-black">{institutes.length}</div>
            <p className="text-[11px] text-slate-500 mt-0.5">Active Coaching Centers</p>
          </div>

          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Active & Operating</span>
            <div className="mt-2 text-2xl font-black text-emerald-500">
              {institutes.filter((i) => i.activationStatus === "ACTIVATED").length}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Fully Activated</p>
          </div>

          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pending Activation</span>
            <div className="mt-2 text-2xl font-black text-amber-500">
              {institutes.filter((i) => i.activationStatus === "PENDING").length}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Awaiting Founder Setup</p>
          </div>

          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Enrolled Students</span>
            <div className="mt-2 text-2xl font-black text-sky-500">
              {institutes.reduce((sum, i) => sum + Number(i.totalStudents || 0), 0)}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Across All Tuitions</p>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="relative w-full sm:w-80">
            <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by tuition name, founder, or subdomain..."
              className={`w-full rounded-2xl border pl-9 pr-4 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-xs font-bold text-slate-400 uppercase">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`rounded-2xl border px-3 py-2 text-xs font-bold focus:outline-none ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-200" : "border-slate-200 bg-white text-slate-700"}`}
            >
              <option value="ALL">All Statuses</option>
              <option value="ACTIVATED">Activated</option>
              <option value="PENDING">Pending Activation</option>
            </select>
          </div>
        </div>

        {/* Tuitions Portfolio Table */}
        <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <table className="w-full text-left text-xs">
            <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
              <tr>
                <th className="px-4 py-3.5">Tuition Institute</th>
                <th className="px-4 py-3.5">Founder / Contact</th>
                <th className="px-4 py-3.5">Plan & Subdomain</th>
                <th className="px-4 py-3.5">Customized Modules</th>
                <th className="px-4 py-3.5">Activation Status</th>
                <th className="px-4 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredInstitutes.map((inst) => {
                const isActivated = inst.activationStatus === "ACTIVATED";
                const mods = inst.enabledModules || {};
                const activeCount = Object.values(mods).filter(Boolean).length;

                return (
                  <tr key={inst.id} className="hover:bg-slate-500/5 transition">
                    <td className="px-4 py-3.5">
                      <p className="font-bold text-slate-900 dark:text-slate-100 text-sm">{inst.name}</p>
                      <p className="text-[11px] text-slate-400">{inst.city}</p>
                    </td>

                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-slate-800 dark:text-slate-200">{inst.ownerName}</p>
                      <p className="text-[10px] text-slate-400">{inst.ownerEmail}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{inst.ownerPhone}</p>
                    </td>

                    <td className="px-4 py-3.5">
                      <span className="rounded-md bg-sky-500/10 px-2 py-0.5 text-[10px] font-bold text-sky-600 dark:text-sky-400">
                        {inst.plan || "GROWTH"}
                      </span>
                      <p className="text-[11px] font-mono text-slate-500 mt-1">{inst.subdomain}.classnx.com</p>
                      {inst.plan === "FREE_TRIAL" && (
                        <div className="mt-1.5 text-[10px] leading-4 text-slate-500">
                          <p>Trial: {formatDate(inst.trialStartDate)} – {formatDate(inst.trialExpiryDate)}</p>
                          {inst.extensionPending && (
                            <span className="font-bold text-amber-600 dark:text-amber-400">Extension pending</span>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/super-admin/tuitions/${encodeURIComponent(inst.id)}/modules`)}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 dark:border-slate-700 px-2.5 py-1 text-xs font-semibold hover:border-sky-500 transition"
                      >
                        <FaSlidersH className="text-sky-500" />
                        <span>{activeCount} of {TUITION_MODULE_DEFINITIONS.length} Modules Active</span>
                      </button>
                    </td>

                    <td className="px-4 py-3.5">
                      {isActivated ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold text-xs">
                          <FaCheckCircle /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-bold text-xs">
                          <FaClock /> Pending Activation
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!isActivated ? (
                          <button
                            type="button"
                            onClick={() => handleResendActivation(inst)}
                            className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-bold text-sky-500 hover:bg-sky-500/20 transition"
                            title="Resend the founder activation email"
                          >
                            <FaEnvelope /> Invite
                          </button>
                        ) : (
                          <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">Founder activated</span>
                        )}

                        <button
                          type="button"
                          onClick={() => navigate(`/super-admin/tuitions/${encodeURIComponent(inst.id)}/modules`)}
                          className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600"
                          title="Module Configuration"
                        >
                          <FaSlidersH />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteInstitute(inst.id)}
                          className="rounded-lg p-1.5 text-rose-500 hover:text-rose-600"
                          title="Remove Institute"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* -------------------------------------------------------------
          MODAL: ONBOARD NEW TUITION INSTITUTE
      ------------------------------------------------------------- */}
      {onboardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border p-6 sm:p-8 shadow-2xl ${isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <h3 className="text-xl font-black flex items-center gap-2">
              <FaBuilding className="text-sky-500" /> Onboard Tuition / Coaching Institute
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Super Admin onboarding will automatically generate an email activation invitation for the founder.
            </p>

            <form onSubmit={handleOnboardSubmit} className="mt-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Institute Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter institute name"
                    value={onboardForm.name}
                    onChange={(e) => setOnboardForm({ ...onboardForm, name: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Subdomain Slug</label>
                  <input
                    type="text"
                    placeholder="e.g. apexiit"
                    value={onboardForm.subdomain}
                    onChange={(e) => setOnboardForm({ ...onboardForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "") })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Founder / Owner Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Dr. Rajeshwar Sharma"
                    value={onboardForm.ownerName}
                    onChange={(e) => setOnboardForm({ ...onboardForm, ownerName: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Founder Email (For Invite) *</label>
                  <input
                    type="email"
                    required
                    placeholder="director@apexiit.com"
                    value={onboardForm.ownerEmail}
                    onChange={(e) => setOnboardForm({ ...onboardForm, ownerEmail: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Founder Mobile</label>
                  <input
                    type="text"
                    placeholder="+91 98765 43210"
                    value={onboardForm.ownerPhone}
                    onChange={(e) => setOnboardForm({ ...onboardForm, ownerPhone: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none focus:border-sky-500 ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">City / Location</label>
                  <input
                    type="text"
                    value={onboardForm.city}
                    onChange={(e) => setOnboardForm({ ...onboardForm, city: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">SaaS Subscription Tier</label>
                  <select
                    value={onboardForm.plan}
                    onChange={(e) => setOnboardForm({ ...onboardForm, plan: e.target.value })}
                    className={`w-full rounded-xl border px-3 py-2 text-xs font-semibold focus:outline-none ${isDarkMode ? "border-slate-700 bg-slate-950 text-white" : "border-slate-300 bg-white text-slate-900"}`}
                  >
                    <option value="FREE_TRIAL">7-Day Free Trial</option>
                    {SUBSCRIPTION_PLANS.map((plan) => (
                      <option key={plan.value} value={plan.value}>
                        {plan.name} — {plan.duration} ({formatInr(plan.price)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Module Toggles Section */}
              <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-bold uppercase text-sky-500">
                    Customizable Feature Modules for this Tuition
                  </h4>
                  <span className="text-[10px] text-slate-400">Toggle enabled features on/off</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-2.5">
                  {TUITION_MODULE_DEFINITIONS.map((mod) => {
                    const isChecked = onboardForm.enabledModules[mod.key] !== false;
                    return (
                      <label
                        key={mod.key}
                        className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition ${isChecked ? "border-sky-500/50 bg-sky-500/5" : "border-slate-200 dark:border-slate-800 opacity-60"}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            setOnboardForm((prev) => ({
                              ...prev,
                              enabledModules: {
                                ...prev.enabledModules,
                                [mod.key]: !isChecked,
                              },
                            }));
                          }}
                          className="mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        <div className="min-w-0">
                          <p className="font-bold text-xs">{mod.label}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">{mod.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setOnboardModal(false)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-sky-600 px-5 py-2 text-xs font-bold text-white hover:bg-sky-700 transition"
                >
                  Complete Onboarding & Issue Activation Email
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
